import './styles.scss';
// AG Grid modules are registered once via the shared rulesAgGridModules import in RulesConfig.
import React, { useState, useMemo, useContext, useCallback, useRef, useEffect } from 'react';
import type { GridApi } from 'ag-grid-community';
import { AgGridBase, useClientExternalTextSearch } from 'Components/organisms/AgGridBase';
import { Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { RiCloseLine } from '@react-icons/all-files/ri/RiCloseLine';
import { ConfirmationModal } from 'Components/molecules';
import { DropdownComponent } from 'Components/molecules';
import { JsonConfigEditor } from 'Components/molecules/JsonConfigEditor';
import { createTagSpec, updateTagSpec, deleteTagSpecById } from 'Apis/library';
import { normalizeTagSpecList } from 'Core-utils/helpers/tagSpecList.util';
import { invalidateTagSpecsCatalog, useTagSpecsCatalog } from 'Hooks/useTagSpecsCatalog';
import {
    YES_DELETE_IT,
    DEFAULT_ERROR_MSG,
    TAGSPECS_SEARCH_PLACEHOLDER,
    TAGSPECS_ADD_BTN,
    TAGSPECS_REFRESH_BTN,
    TAGSPECS_DELETE_MODAL_TITLE,
    TAGSPECS_DELETE_MODAL_BODY,
    TAGSPECS_MODAL_EDIT_TITLE,
    TAGSPECS_MODAL_CREATE_TITLE,
    TAGSPECS_LABEL_NAME,
    TAGSPECS_LABEL_NOTIFY,
    TAGSPECS_LABEL_CONNECTOR,
    TAGSPECS_LABEL_RULE,
    TAGSPECS_LABEL_DESCRIPTION,
    TAGSPECS_NAME_PLACEHOLDER,
    TAGSPECS_DESC_PLACEHOLDER,
    TAGSPECS_NOTIFY_ENABLED,
    TAGSPECS_NOTIFY_DISABLED,
    TAGSPECS_SELECT_CONNECTOR,
    TAGSPECS_SELECT_CONNECTOR_FIRST,
    TAGSPECS_NO_RULES_FOR_CONNECTOR,
    TAGSPECS_SELECT_RULE,
    TAGSPECS_CANCEL_BTN,
    TAGSPECS_SAVING,
    TAGSPECS_SAVE_CHANGES,
    TAGSPECS_CREATE_BTN,
    TAGSPECS_DELETED,
    TAGSPECS_UPDATED,
    TAGSPECS_CREATED,
} from 'Core-utils/constants/constants';
import { useI18n } from 'Hooks/useI18n';
import { ViewQueryModal } from 'Components/organisms/ViewQueryModal';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import { SYSTEM_TID, TagSpecDto } from '@ampsec/platform-client';
import { tryParse, isJsony } from 'Core-utils/helpers/ruleBuilderHelpers';
import { RuleOption } from 'Core-utils/types/rules.types';
import { TagSpecFormState } from 'Core-utils/types/TagSpecsConfig.types';
import { emptyTagSpecForm, formFromTagSpec } from 'Core-utils/helpers/tagSpecHelpers';
import { getTagSpecsGridColumns } from './getTagSpecsGridColumns';

// ─── Stable getRowId (defined outside component to avoid recreation per render) ──
const getRowId = (params: { data?: TagSpecDto }) => String(params.data?.id ?? '');

interface TagSpecsConfigProps {
    refresh?: boolean;
    /** null while rules are loading; RuleOption[] once loaded */
    ruleOptions: RuleOption[] | null;
    connectorMap: Record<string, string>;
}

const TagSpecsConfig = ({ refresh, ruleOptions, connectorMap }: TagSpecsConfigProps) => {
    const { t } = useI18n();
    const { addToast } = useContext(ToastsContext);
    const tid = useSelector((state: RootState) => state.auth.selectedTenant.id) as string | undefined;
    const { extractedData: connectorsList } = useConnectorData();

    const {
        tagSpecs: tagSpecCatalog,
        isLoading: isTagSpecsLoading,
        refresh: refreshTagSpecs,
    } = useTagSpecsCatalog({ enabled: !!tid });

    const normalizedTagSpecs = useMemo(
        () => normalizeTagSpecList(tagSpecCatalog).filter((spec) => spec.tid !== SYSTEM_TID),
        [tagSpecCatalog],
    );

    const bumpTagSpecsCatalog = useCallback(() => {
        invalidateTagSpecsCatalog(tid);
        refreshTagSpecs();
    }, [tid, refreshTagSpecs]);

    useEffect(() => {
        if (refresh !== undefined) {
            bumpTagSpecsCatalog();
        }
        // Parent refresh flag only — not tid or bumpTagSpecsCatalog (would loop).
    }, [refresh]);

    // ── Grid API ref + external search ────────────────────────────────────────
    const gridApiRef = useRef<GridApi<TagSpecDto> | null>(null);
    const { handleSearchChange, isExternalFilterPresent, doesExternalFilterPass } =
        useClientExternalTextSearch<TagSpecDto>({
            fields: [
                (row) => row.name ?? '',
                (row) => row.rid ?? '',
                (row) => row.description ?? '',
            ],
            onFilterChanged: () => gridApiRef.current?.onFilterChanged(),
        });

    // ── Modal state ──────────────────────────────────────────────────────────
    const [showTagSpecDeleteModal, setShowTagSpecDeleteModal] = useState(false);
    const [tagSpecToDelete, setTagSpecToDelete] = useState<string | null>(null);
    const [showTagSpecModal, setShowTagSpecModal] = useState(false);
    const [editingTagSpec, setEditingTagSpec] = useState<TagSpecDto | null>(null);
    const [saving, setSaving] = useState(false);
    const [viewingConfig, setViewingConfig] = useState<TagSpecDto | null>(null);

    // ── Form state — UI-only selectedCid is separate from the submitted form ──
    const [form, setForm] = useState<TagSpecFormState>(emptyTagSpecForm());
    const [selectedCid, setSelectedCid] = useState('');

    const patch = (p: Partial<TagSpecFormState>) => setForm((f) => ({ ...f, ...p }));

    // ── Open modal — initialise form synchronously so there's no empty flash ──
    // ruleOptions must be the current value at open time; it is NOT added as a
    // useEffect dep to avoid resetting an in-progress edit if rules reload.
    const handleOpenTagSpecModal = useCallback(
        (spec: TagSpecDto | null = null) => {
            setEditingTagSpec(spec);
            if (spec) {
                const resolved = formFromTagSpec(spec, ruleOptions ?? []);
                setForm(resolved.form);
                setSelectedCid(resolved.selectedCid);
            } else {
                setForm(emptyTagSpecForm());
                setSelectedCid('');
            }
            setShowTagSpecModal(true);
        },
        [ruleOptions],
    );

    const handleCloseTagSpecModal = useCallback(() => {
        setShowTagSpecModal(false);
        setEditingTagSpec(null);
    }, []);

    // ── Rules filtered by the selected connector ──────────────────────────────
    const rulesForSelectedCid: RuleOption[] = useMemo(() => {
        const options = ruleOptions ?? [];
        if (!selectedCid) {
            return options;
        }
        return options.filter((r) => r.cid === selectedCid);
    }, [ruleOptions, selectedCid]);

    // ── Form validation ───────────────────────────────────────────────────────
    // configError === null means valid (empty field is also valid)
    const isTagFormValid =
        form.name.trim().length > 0 &&
        form.rid.trim().length > 0 &&
        form.configError === null;

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSaveTagSpec = useCallback(async () => {
        const { value: config } = tryParse(form.configText);
        const payload = {
            tid,
            name: form.name,
            rid: form.rid,
            description: form.description || null,
            // omit config entirely when field is empty (avoids sending config: null)
            config: isJsony(config) ? config : undefined,
            notify: form.notify,
        };

        setSaving(true);
        const apiCall = editingTagSpec
            ? updateTagSpec(editingTagSpec.id, payload)
            : createTagSpec(payload);

        try {
            await apiCall;
            addToast({
                severity: 'success',
                body: editingTagSpec
                    ? t(TAGSPECS_UPDATED, 'Tag Spec updated successfully')
                    : t(TAGSPECS_CREATED, 'Tag Spec created successfully'),
            });
            bumpTagSpecsCatalog();
            handleCloseTagSpecModal();
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setSaving(false);
        }
    }, [form, editingTagSpec, tid, addToast, handleCloseTagSpecModal, bumpTagSpecsCatalog, t]);

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleTagSpecDeleteClick = useCallback((id: string) => {
        setTagSpecToDelete(id);
        setShowTagSpecDeleteModal(true);
    }, []);

    const confirmTagSpecDelete = useCallback(async () => {
        if (!tagSpecToDelete) {
            return;
        }
        try {
            await deleteTagSpecById(tagSpecToDelete);
            addToast({ severity: 'success', body: t(TAGSPECS_DELETED, 'Tag Spec deleted successfully') });
            bumpTagSpecsCatalog();
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setShowTagSpecDeleteModal(false);
            setTagSpecToDelete(null);
        }
    }, [tagSpecToDelete, addToast, bumpTagSpecsCatalog, t]);

    // ── Stable callback refs for column defs ──────────────────────────────────
    const handleOpenTagSpecModalRef = useRef(handleOpenTagSpecModal);
    handleOpenTagSpecModalRef.current = handleOpenTagSpecModal;

    const handleTagSpecDeleteClickRef = useRef(handleTagSpecDeleteClick);
    handleTagSpecDeleteClickRef.current = handleTagSpecDeleteClick;

    // Refs so cell renderers always read the latest values without requiring
    // useMemo re-creation of column definitions.
    const ruleOptionsRef = useRef(ruleOptions ?? []);
    ruleOptionsRef.current = ruleOptions ?? [];

    const connectorMapRef = useRef(connectorMap);
    connectorMapRef.current = connectorMap;

    // ── Column definitions (extracted to getTagSpecsGridColumns.tsx) ──────────
    const columnDefs = useMemo(
        () =>
            getTagSpecsGridColumns(
                t,
                ruleOptionsRef,
                connectorMapRef,
                handleOpenTagSpecModalRef,
                handleTagSpecDeleteClickRef,
                setViewingConfig,
            ),
        [t],
    );

    return (
        <div className="d-flex flex-column h-100 ps-0 pe-3">
            {/* ── Toolbar ──────────────────────────────────────────────────── */}
            <div className="d-flex align-items-center gap-3 tagspecs-config__toolbar">
                <input
                    type="text"
                    className="form-control form-control-sm tagspecs-config__search-input"
                    placeholder={t(TAGSPECS_SEARCH_PLACEHOLDER, 'Search by name, rid, description…')}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
                <Button
                    size="sm"
                    className="d-flex align-items-center gap-2 text-white rounded-2 tagspecs-config__add-btn"
                    onClick={() => handleOpenTagSpecModal()}
                >
                    {t(TAGSPECS_ADD_BTN, '+ Add Tag Spec')}
                </Button>
                <Button
                    className="d-flex align-items-center gap-2 ms-auto tagspecs-config__refresh-btn"
                    size="sm"
                    disabled={isTagSpecsLoading}
                    onClick={bumpTagSpecsCatalog}
                >
                    {t(TAGSPECS_REFRESH_BTN, '↻ Refresh')}
                </Button>
            </div>

            {/* ── Grid ─────────────────────────────────────────────────────── */}
            <div className="tagspecs-config__table-wrapper">
                <AgGridBase.Root fullWidth>
                    <AgGridBase.Header hidden />
                    <AgGridBase.Table<TagSpecDto>
                        columnDefs={columnDefs}
                        dataMode="client"
                        rowData={normalizedTagSpecs}
                        showSidebar={false}
                        movableColumns={false}
                        wrapperClassName="rules-ag-grid__wrapper--theme-findings ag-findings-grid__wrapper ag-grid-theme--amp-dark"
                        defaultColDef={{
                            wrapHeaderText: false,
                            autoHeaderHeight: false,
                            minWidth: 80,
                            cellStyle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                        }}
                        agGridProps={{
                            loading: isTagSpecsLoading,
                            getRowId,
                            isExternalFilterPresent,
                            doesExternalFilterPass,
                            onGridReady: (e) => { gridApiRef.current = e.api; },
                            onGridPreDestroyed: () => { gridApiRef.current = null; },
                            suppressContextMenu: true,
                            animateRows: false,
                            rowHeight: 72,
                        }}
                    />
                </AgGridBase.Root>
            </div>

            <ConfirmationModal
                modalTitle={t(TAGSPECS_DELETE_MODAL_TITLE, 'Delete Tag Spec')}
                modalBodyTitle={t(
                    TAGSPECS_DELETE_MODAL_BODY,
                    'Are you sure you want to delete this tag spec? This action cannot be undone.',
                )}
                onPrimaryButtonClick={confirmTagSpecDelete}
                primaryButtonLabel={t(YES_DELETE_IT, 'Yes, Delete It')}
                show={showTagSpecDeleteModal}
                onClose={() => {
                    setShowTagSpecDeleteModal(false);
                    setTagSpecToDelete(null);
                }}
                buttonVariant="danger"
            />

            <ViewQueryModal
                onClose={() => setViewingConfig(null)}
                show={!!viewingConfig}
                json={viewingConfig?.config ? [viewingConfig.config] : []}
                isFooter
                title={`Tag Spec Config: ${viewingConfig?.name ?? ''}`}
            />

            {/* ── Tag Spec create / edit modal ──────────────────────────────── */}
            <Modal
                show={showTagSpecModal}
                onHide={handleCloseTagSpecModal}
                size="lg"
                centered
                contentClassName="bg-body-color border-secondary rounded-3"
            >
                <Modal.Header className="border-secondary px-4 py-3 tagspec-modal__header">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                        <span className="tagspec-modal__icon">⌗</span>
                        <span className="tagspec-modal__title">
                            {editingTagSpec
                                ? t(TAGSPECS_MODAL_EDIT_TITLE, 'Edit Tag Spec')
                                : t(TAGSPECS_MODAL_CREATE_TITLE, 'New Tag Spec')}
                        </span>
                        {editingTagSpec && (
                            <code className="tagspec-modal__id-badge">{editingTagSpec.id}</code>
                        )}
                    </div>
                    <RiCloseLine
                        size={22}
                        className="tagspec-modal__close-icon"
                        onClick={handleCloseTagSpecModal}
                    />
                </Modal.Header>

                <Modal.Body className="px-4 py-4 custom-scrollbar bg-body-color">
                    <Form>
                        <Row className="mb-4 g-3">
                            <Col md={9}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 tagspec-modal__label">
                                    {t(TAGSPECS_LABEL_NAME, 'Name')} *
                                </label>
                                <Form.Control
                                    placeholder={t(TAGSPECS_NAME_PLACEHOLDER, 'e.g. training-due-date-tag')}
                                    value={form.name}
                                    onChange={(e) => patch({ name: e.target.value })}
                                    style={{ fontSize: '13px', height: '40px' }}
                                    className="rule-search-input"
                                    autoFocus
                                />
                            </Col>
                            <Col md={3}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 tagspec-modal__label">
                                    {t(TAGSPECS_LABEL_NOTIFY, 'Notify')}
                                </label>
                                <div className="d-flex align-items-center gap-3 tagspec-modal__notify-wrapper">
                                    <Form.Check
                                        type="switch"
                                        id="notify-switch"
                                        checked={form.notify}
                                        onChange={(e) => patch({ notify: e.target.checked })}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <span
                                        className={`tagspec-modal__notify-badge tagspec-modal__notify-badge--${form.notify ? 'on' : 'off'}`}
                                    >
                                        {form.notify
                                            ? t(TAGSPECS_NOTIFY_ENABLED, 'Enabled')
                                            : t(TAGSPECS_NOTIFY_DISABLED, 'Disabled')}
                                    </span>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={6}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 tagspec-modal__label">
                                    {t(TAGSPECS_LABEL_CONNECTOR, 'Connector')}
                                </label>
                                <div style={{ height: '40px' }} className="rule-search-input rounded">
                                    <DropdownComponent
                                        value={
                                            connectorMap[selectedCid] ||
                                            t(TAGSPECS_SELECT_CONNECTOR, 'Select Connector…')
                                        }
                                        dropDownItems={(connectorsList || []).map((c) => ({
                                            id: c.cid,
                                            displayValue: c.title || c.cid,
                                            value: c.cid,
                                        }))}
                                        onChange={(val) => {
                                            setSelectedCid(val ?? '');
                                            patch({ rid: '' });
                                        }}
                                        variant="filled"
                                        disableToggle={!!editingTagSpec}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 tagspec-modal__label">
                                    {t(TAGSPECS_LABEL_RULE, 'Rule (RID)')} *
                                </label>
                                <div style={{ height: '40px' }} className="rule-search-input rounded">
                                    <DropdownComponent
                                        value={
                                            form.rid
                                                ? ((ruleOptions ?? []).find((r) => r.value === form.rid)?.displayValue ?? form.rid)
                                                : (ruleOptions === null
                                                ? t('TAGSPECS_LOADING_RULES', 'Loading rules…')
                                                : !selectedCid
                                                ? t(TAGSPECS_SELECT_CONNECTOR_FIRST, 'Select a connector first')
                                                : rulesForSelectedCid.length === 0
                                                ? t(TAGSPECS_NO_RULES_FOR_CONNECTOR, 'No rules for this connector')
                                                : t(TAGSPECS_SELECT_RULE, 'Select Rule…'))
                                        }
                                        dropDownItems={rulesForSelectedCid}
                                        onChange={(val) => patch({ rid: val ?? '' })}
                                        variant="filled"
                                        isStartCase={false}
                                        disableToggle={!!editingTagSpec}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div className="mb-4">
                            <label className="ts-modal-label text-uppercase fw-bold text-white mb-2 tagspec-modal__label">
                                {t(TAGSPECS_LABEL_DESCRIPTION, 'Description')}
                            </label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={form.description}
                                onChange={(e) => patch({ description: e.target.value })}
                                placeholder={t(TAGSPECS_DESC_PLACEHOLDER, 'Optional description…')}
                                style={{ fontSize: '13px' }}
                                className="rule-search-input"
                            />
                        </div>

                        <JsonConfigEditor
                            value={form.configText}
                            error={form.configError}
                            onChange={(configText, configError) => patch({ configText, configError })}
                        />
                    </Form>
                </Modal.Body>

                <Modal.Footer className="border-secondary px-4 py-3 tagspec-modal__footer">
                    <Button
                        disabled={saving}
                        onClick={handleCloseTagSpecModal}
                        className="tagspec-modal__cancel-btn"
                    >
                        {t(TAGSPECS_CANCEL_BTN, '✕ Cancel')}
                    </Button>
                    <Button
                        disabled={!isTagFormValid || saving}
                        onClick={handleSaveTagSpec}
                        className={`tagspec-modal__save-btn tagspec-modal__save-btn--${!isTagFormValid || saving ? 'disabled' : 'active'}`}
                    >
                        {saving
                            ? t(TAGSPECS_SAVING, 'Saving…')
                            : editingTagSpec
                            ? t(TAGSPECS_SAVE_CHANGES, 'Save Changes')
                            : t(TAGSPECS_CREATE_BTN, 'Create Tag Spec')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TagSpecsConfig;
